import * as React from "react";
import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Text,
  TextField,
  PrimaryButton,
  DefaultButton,
  IconButton,
  TooltipHost,
  Spinner,
  SpinnerSize,
  Icon,
  mergeStyles,
  getTheme,
} from "@fluentui/react";
import { apiBaseUrl } from "../../../auth/authConfig";
import { isSignedIn } from "../../../auth/authDialog";
import { insertMarketSheet } from "../../marketSheet";
import { openMarketMap } from "../../mapDialog";
import { MarketOption, MarketsState, marketFormulaCode } from "../types";
import { resultListStyle } from "./CompanySearch";

const API_URL = apiBaseUrl();
const theme = getTheme();

const RESULT_LIMIT = 20;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;

const TYPE_LABEL: Record<MarketOption["type"], string> = {
  state: "State",
  county: "County",
  msa: "MSA",
  zip: "Zip",
};

const rowStyle = mergeStyles({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  padding: "8px 12px",
  border: "none",
  borderBottom: `1px solid ${theme.palette.neutralLighter}`,
  backgroundColor: theme.palette.white,
  textAlign: "left",
  cursor: "pointer",
  selectors: {
    ":last-child": { borderBottom: "none" },
    ":hover": { backgroundColor: theme.palette.neutralLighterAlt },
  },
});

const selectedRowStyle = mergeStyles({
  backgroundColor: theme.palette.themeLighterAlt,
  selectors: {
    ":hover": { backgroundColor: theme.palette.themeLighter },
  },
});

const typeBadgeStyle = mergeStyles({
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.4px",
  padding: "1px 5px",
  borderRadius: 3,
  color: theme.palette.neutralSecondary,
  border: `1px solid ${theme.palette.neutralTertiaryAlt}`,
  flexShrink: 0,
});

const cardStyle = mergeStyles({
  border: `1px solid ${theme.palette.neutralLight}`,
  borderRadius: "4px",
  padding: "8px 12px",
  backgroundColor: theme.palette.neutralLighterAlt,
});

const codeStyle = mergeStyles({
  fontFamily: "Consolas, Menlo, monospace",
  fontSize: 12,
});

// Deposits arrive in thousands USD.
function formatDeposits(thousands: number): string {
  const millions = thousands / 1000;
  if (millions >= 1000) return `$${(millions / 1000).toFixed(1)}B`;
  return `$${millions.toFixed(0)}M`;
}

const MarketRow: React.FunctionComponent<{
  option: MarketOption;
  selected: boolean;
  onSelect: (option: MarketOption) => void;
}> = ({ option, selected, onSelect }) => (
  <button
    type="button"
    className={`${rowStyle} ${selected ? selectedRowStyle : ""}`}
    onClick={() => onSelect(option)}
  >
    <span className={typeBadgeStyle}>{TYPE_LABEL[option.type]}</span>
    <Stack grow tokens={{ childrenGap: 2 }} styles={{ root: { minWidth: 0 } }}>
      <Text styles={{ root: { fontWeight: 600 } }}>{option.name}</Text>
      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
        {option.branches.toLocaleString()} branches · {formatDeposits(option.deposits)} deposits
      </Text>
    </Stack>
    <Icon iconName="ChevronRight" styles={{ root: { fontSize: 12, color: theme.palette.neutralTertiary } }} />
  </button>
);

export interface MarketsProps {
  markets: MarketsState;
  onMarketsChange: (state: MarketsState) => void;
}

const Markets: React.FunctionComponent<MarketsProps> = ({ markets, onMarketsChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [inserting, setInserting] = useState(false);
  const [insertNote, setInsertNote] = useState<string | null>(null);
  const [insertError, setInsertError] = useState<string | null>(null);
  const [mapError, setMapError] = useState<string | null>(null);

  // Monotonic id per fetch: a stale response (user kept typing) is dropped.
  const requestIdRef = useRef(0);

  const runSearch = async (query: string) => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/markets/search?q=${encodeURIComponent(query)}&limit=${RESULT_LIMIT}`);
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const results: MarketOption[] = await response.json();
      if (requestId !== requestIdRef.current) return;
      onMarketsChange({ ...markets, query, searchedQuery: query, results });
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setError(`Search failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    const query = markets.query.trim();
    if (query === markets.searchedQuery) return;
    if (query.length < MIN_QUERY_LENGTH) {
      requestIdRef.current++;
      setLoading(false);
      setError(null);
      if (markets.searchedQuery !== null) {
        onMarketsChange({ ...markets, searchedQuery: null, results: [] });
      }
      return;
    }
    const timer = setTimeout(() => runSearch(query), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [markets.query]);

  const selected = markets.selected;
  const code = selected ? marketFormulaCode(selected) : "";
  const yearText = markets.year.trim();
  const yearNumber = /^\d{4}$/.test(yearText) ? Number(yearText) : undefined;
  const yearInvalid = yearText !== "" && yearNumber === undefined;

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleInsert = async () => {
    if (!selected) return;
    setInsertError(null);
    setInsertNote(null);
    setInserting(true);
    try {
      const sheetName = await insertMarketSheet({
        baseName: `Market - ${selected.name}`.slice(0, 31), // Excel sheet-name limit
        code,
        name: selected.name,
        year: yearNumber,
      });
      setInsertNote(`Inserted sheet "${sheetName}".`);
      setTimeout(() => setInsertNote(null), 5000);
    } catch (err) {
      setInsertError(`Insert failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setInserting(false);
    }
  };

  const handleOpenMap = async () => {
    if (!selected) return;
    setMapError(null);
    try {
      await openMarketMap(selected, yearNumber);
    } catch (err) {
      setMapError(err instanceof Error ? err.message : String(err));
    }
  };

  const searched = markets.searchedQuery !== null;
  const exampleFormula = `=SHEETYBANKS.MARKET("${code || "48113"}", {"RANK","NAME","BRANCHES","DEPOSITS","SHARE"}${
    yearNumber ? `, ${yearNumber}` : ""
  })`;

  return (
    <Stack tokens={{ childrenGap: 12 }}>
      <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
        Deposit market share from the FDIC Summary of Deposits survey. Find a county, metro area,
        state or zip, then insert a ranked market-share sheet or use the code in a formula.
      </Text>

      <TextField
        placeholder="Search markets: county, metro area, state, or zip…"
        iconProps={{ iconName: "Search" }}
        value={markets.query}
        onChange={(_e, val) => onMarketsChange({ ...markets, query: val || "" })}
      />

      {loading && <Spinner size={SpinnerSize.medium} label="Searching..." />}
      {error && <Text styles={{ root: { color: theme.palette.redDark } }}>{error}</Text>}

      {!loading && !error && searched && markets.results.length === 0 && (
        <Text styles={{ root: { color: theme.palette.neutralSecondary } }}>
          No markets found matching “{markets.searchedQuery}”.
        </Text>
      )}

      {!loading && !error && markets.results.length > 0 && (
        <div className={resultListStyle}>
          {markets.results.map((option) => (
            <MarketRow
              key={`${option.type}:${option.code}`}
              option={option}
              selected={selected?.type === option.type && selected?.code === option.code}
              onSelect={(o) => onMarketsChange({ ...markets, selected: o })}
            />
          ))}
        </div>
      )}

      {selected && (
        <div className={cardStyle}>
          <Stack tokens={{ childrenGap: 8 }}>
            <Text styles={{ root: { fontWeight: 600 } }}>{selected.name}</Text>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
              <Text variant="small" styles={{ root: { color: theme.palette.neutralSecondary } }}>
                Market code
              </Text>
              <span className={codeStyle}>{code}</span>
              <TooltipHost content={copied ? "Copied!" : "Copy market code"}>
                <IconButton
                  iconProps={{ iconName: copied ? "CheckMark" : "Copy" }}
                  onClick={handleCopy}
                  styles={{
                    root: { height: 24, width: 24 },
                    icon: { fontSize: 12, color: copied ? theme.palette.green : theme.palette.themePrimary },
                  }}
                />
              </TooltipHost>
            </Stack>
            <TextField
              label="Survey year"
              placeholder="Latest"
              value={markets.year}
              onChange={(_e, val) => onMarketsChange({ ...markets, year: val || "" })}
              errorMessage={yearInvalid ? "Enter a four-digit year, or leave blank for the latest survey" : undefined}
              styles={{ root: { width: 120 } }}
            />
            <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
              {exampleFormula}
            </Text>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
              <PrimaryButton
                text={inserting ? "Inserting…" : "Insert market share sheet"}
                iconProps={{ iconName: "PageAdd" }}
                onClick={handleInsert}
                disabled={inserting || yearInvalid || !isSignedIn()}
                styles={{ root: { minWidth: 0, height: 28 }, label: { fontSize: 12 } }}
              />
              <DefaultButton
                text="Map market"
                iconProps={{ iconName: "MapPin" }}
                onClick={handleOpenMap}
                disabled={yearInvalid || !isSignedIn()}
                styles={{ root: { minWidth: 0, height: 28 }, label: { fontSize: 12 } }}
              />
              {!isSignedIn() && (
                <Text variant="xSmall" styles={{ root: { color: theme.palette.neutralTertiary } }}>
                  Sign in first
                </Text>
              )}
            </Stack>
            {mapError && (
              <Text variant="small" styles={{ root: { color: theme.palette.redDark } }}>
                {mapError}
              </Text>
            )}
            {insertNote && (
              <Text variant="small" styles={{ root: { color: theme.palette.green } }}>
                {insertNote}
              </Text>
            )}
            {insertError && (
              <Text variant="small" styles={{ root: { color: theme.palette.redDark } }}>
                {insertError}
              </Text>
            )}
          </Stack>
        </div>
      )}
    </Stack>
  );
};

export default Markets;
